'use client'
import {
  navConfig,
  navConfigAccessories,
  navConfigCollection,
} from '@/components/layout/header/nav-config'
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from '@/components/ui/navigation-menu'
import { useBannerQuery } from '@/hooks/react-query/banner/use-banner'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { cn, getLastSegment, normalizeVietnamese } from '@/lib/utils'
import { useIsBuy1Get1ValueMatchCampaignActive } from '@/lib/utils/campaign/campaign-active'
import { NavConfig } from '@/types/header'
import { MoveRight } from 'lucide-react'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import Link from 'next/link'
import { usePathname, useSearchParams } from 'next/navigation'
import { useMemo } from 'react'

interface NavItemProps {
  href: string
  title: string
  className?: string
  isComingSoon?: boolean
  description?: string
  type?: string
}

interface NavSectionProps {
  section: {
    title: string
    href?: string
    items: NavItemProps[]
  }
  type: string
}

const NavItem = ({
  href,
  title,
  className,
  isComingSoon,
  description,
  type,
}: NavItemProps) => {
  const t = useTranslations('header')
  return (
    <li className='block h-fit w-fit'>
      <Link
        href={href}
        prefetch={
          href === '/collection/ao-nam-choi-the-thao' ? false : undefined
        }
        aria-disabled={isComingSoon}
        tabIndex={isComingSoon ? -1 : 0}
        className={cn(
          'group line-clamp-2 whitespace-nowrap text-xs text-neutral-600 transition-colors duration-200 hover:text-primary xl:text-sm 2xl:text-base',
          className,
          isComingSoon &&
            'pointer-events-none cursor-default text-neutral-400 hover:text-neutral-400'
        )}
        onClick={(e) => isComingSoon && e.preventDefault()}
        aria-label={
          isComingSoon ? `${t(title)} (Sắp ra mắt)` : `Đi tới: ${t(title)}`
        }
        ga-tracking-value={`menu__${type}__${getLastSegment(title)}`}
        ga-tracking-label={t(title)}
      >
        {t(title)}
        {isComingSoon && <p className='italic'>(Coming soon)</p>}
        {description && <p className='text-sm italic'>{t(description)}</p>}
      </Link>
    </li>
  )
}

const NavSection = ({ section, type }: NavSectionProps) => {
  const t = useTranslations('header')
  return (
    <ul className='space-y-2 2xl:space-y-5'>
      <li>
        <Link
          href={section?.href || '/'}
          className={cn('w-fit', !section?.href && 'cursor-default')}
          onClick={(e) => !section?.href && e.preventDefault()}
          aria-label={section.href ? `Đi tới: ${section.title}` : undefined}
          ga-tracking-value={`menu__${type}__${getLastSegment(section.title)}`}
          ga-tracking-label={t(section.title)}
        >
          <h4
            className={cn(
              'group flex items-center whitespace-nowrap text-sm font-bold uppercase text-neutral-900 transition-colors duration-200 2xl:text-base',
              section.href && 'hover:text-primary'
            )}
          >
            {t(`${section.title}`)}
            {section?.href && (
              <MoveRight className='ml-3 text-primary transition-transform duration-300 group-hover:translate-x-2 max-2xl:h-4 max-2xl:w-4' />
            )}
          </h4>
        </Link>
      </li>
      {section.items.map((item) => (
        <NavItem key={item.href} {...item} type={type} />
      ))}
    </ul>
  )
}

const FeaturedContent = ({
  featured,
  className,
  type,
}: {
  featured?: {
    title: string
    href: string
    imageAlt: string
    imageSrc: string
    tag?: string
  }[]
  className?: string
  type?: string
}) => {
  const t = useTranslations('header')
  const { data: apiBanners } = useBannerQuery()

  const bannersForType = type && apiBanners ? apiBanners[type] : undefined

  const validBannersFromApi = bannersForType?.filter(
    (banner) => banner.link && banner.title && banner.image?.desktop
  )

  const hasApiBanners = validBannersFromApi && validBannersFromApi.length > 0

  const bannersToDisplay = hasApiBanners
    ? validBannersFromApi.map((item) => ({
        title: item.title,
        href: item.link,
        imageAlt: item.alt,
        imageSrc: item.image.desktop,
        tag: undefined,
      }))
    : featured

  if (!bannersToDisplay || bannersToDisplay.length === 0) {
    return null
  }

  return (
    <div
      className={cn(
        'flex basis-[28%] flex-col gap-5 border-l border-l-neutral-300 px-8 py-8 2xl:px-10 2xl:pb-17.5',
        className
      )}
    >
      {bannersToDisplay.map((item, index) => (
        <Link
          key={item.href}
          href={item.href}
          className='group relative flex-1 overflow-hidden rounded-lg'
          ga-tracking-value={`menu__banner__${type}__vitri-${index + 1}_${normalizeVietnamese(item.title)}`}
          ga-tracking-label={item.title}
        >
          <Image
            width={340}
            height={185}
            src={ImageHelper.getImageUrl(item.imageSrc, '', 680)}
            alt={item.title || 'Hình ảnh nổi bật'}
            className='aspect-[340/185] h-full w-full object-cover transition-transform duration-300 ease-in-out hover:scale-105 xl:h-full'
          />
          <p className='absolute bottom-3 left-4 line-clamp-2 font-sans text-sm font-bold text-background 2xl:text-lg'>
            {hasApiBanners ? item.title : t(item.title)}
          </p>
          {item.tag && (
            <p className='absolute right-4 top-3 rounded-full bg-red-600 px-2 py-0.5 text-xs font-medium text-background'>
              {item.tag}
            </p>
          )}
        </Link>
      ))}
    </div>
  )
}

type NavLinkGroup = {
  key: string
  href: string
  childHrefs: string[]
}

const AUTUMN_WINTER_HREF = '/collection/do-thu-dong?itm_source=navbar'
const OUTLET_HREF = '/collection/giam-gia?itm_source=navbar'

const normalizePath = (href: string) =>
  href.split(/[?#]/)[0].replace(/\/+$/, '') || '/'

const stripLocale = (pathname: string) =>
  normalizePath(pathname.replace(/^\/en(?=\/|$)/, ''))

const navLinkGroups: NavLinkGroup[] = [
  { key: 'autumn_winter', href: AUTUMN_WINTER_HREF, childHrefs: [] },
  ...Object.entries({
    ...navConfig,
    ...navConfigCollection,
    ...navConfigAccessories,
  } as NavConfig).map(([key, content]) => ({
    key,
    href: content.href,
    childHrefs: [
      ...content.sections.flatMap((section) => [
        section.href,
        ...section.items.map((item) => item?.href),
      ]),
      ...(content.footer?.flatMap((f) => f.items.map((item) => item.href)) ??
        []),
    ].filter((href): href is string => !!href),
  })),
  { key: 'outlet', href: OUTLET_HREF, childHrefs: [] },
]

const getGenderType = (href: string) =>
  new URLSearchParams(href.split('?')[1]?.split('#')[0]).get('gender_type')

// Link con cùng path nhưng khác gender_type (vd do-casual nam/nữ) -> chấm điểm để chọn đúng menu cha:
// 2 = khớp gender_type, 1 = link không phân biệt gender, 0 = khác gender_type
const getChildMatchScore = (href: string, genderType: string | null) => {
  const hrefGender = getGenderType(href)
  if (!hrefGender) return 1
  return hrefGender === genderType ? 2 : 0
}

// Ưu tiên item có href trùng khớp, sau đó tới item chứa link con trùng khớp (ưu tiên khớp gender_type)
const getActiveNavKey = (pathname: string, genderType: string | null) => {
  const path = stripLocale(pathname)
  if (path === '/') return undefined

  const exactKey = navLinkGroups.find(
    (group) => normalizePath(group.href) === path
  )?.key
  if (exactKey) return exactKey

  let bestKey: string | undefined
  let bestScore = -1
  for (const group of navLinkGroups) {
    for (const href of group.childHrefs) {
      if (normalizePath(href) !== path) continue
      const score = getChildMatchScore(href, genderType)
      if (score > bestScore) {
        bestKey = group.key
        bestScore = score
      }
    }
  }
  return bestKey
}

const activeTriggerClass = 'before:w-full'

export function MainNav() {
  const pathname = usePathname()
  const genderType = useSearchParams().get('gender_type')
  const activeKey = useMemo(
    () => getActiveNavKey(pathname, genderType),
    [pathname, genderType]
  )
  const isBlackFridayActive = useIsBuy1Get1ValueMatchCampaignActive()
  const t = useTranslations('header')

  return (
    <nav
      aria-label='Thanh điều hướng chính'
      className='hidden h-full items-center lg:block'
    >
      <NavigationMenu key={pathname} className='z-[60] items-stretch'>
        <NavigationMenuList role='menu'>
          <NavigationMenuItem role='menuitem'>
            <Link
              href={AUTUMN_WINTER_HREF}
              className={cn(
                navigationMenuTriggerStyle(),
                'relative font-medium text-primary before:bg-primary',
                isBlackFridayActive && 'text-neutral-900',
                activeKey === 'autumn_winter' && activeTriggerClass
              )}
              ga-tracking-value='menu__autumn_winter'
              ga-tracking-label={t('nav.section.autumn_winter')}
            >
              {t('nav.section.autumn_winter')}
            </Link>
          </NavigationMenuItem>

          {/* <NavigationMenuItem role='menuitem'>
            <Link
              href='/collection/ban-chay-nhat?itm_source=navbar'
              className={cn(
                navigationMenuTriggerStyle(),
                'relative font-medium text-primary before:bg-primary'
              )}
              ga-tracking-value='menu__best_seller'
              ga-tracking-label={t('nav.section.best_seller')}
            >
              {t('nav.section.best_seller')}
            </Link>
          </NavigationMenuItem> */}

          {Object.entries(navConfig).map(([category, content]) => (
            <NavigationMenuItem role='menuitem' key={category}>
              <NavigationMenuTrigger asChild>
                <Link
                  suppressHydrationWarning
                  href={content.href}
                  className={cn(
                    navigationMenuTriggerStyle(),
                    activeKey === category && activeTriggerClass
                  )}
                  aria-label={`Mở Trang: ${category}`}
                  ga-tracking-value={`menu__${getLastSegment(category)}`}
                  ga-tracking-label={t(category)}
                >
                  {t(category)}
                </Link>
              </NavigationMenuTrigger>

              <NavigationMenuContent className='w-full'>
                <div className='flex w-full bg-background'>
                  <div className='flex basis-[72%] flex-col justify-between'>
                    <div className='flex justify-between space-x-6 px-10 pb-8 pt-8 2xl:space-x-16 2xl:px-20 2xl:pb-17.5'>
                      {content.sections.map((section, idx) => (
                        <NavSection
                          key={idx}
                          section={section}
                          type={content.type}
                        />
                      ))}
                    </div>

                    {content?.footer?.map((footerItem, idx) => (
                      <ul
                        className='flex items-center bg-neutral-100 md:px-10 2xl:px-20'
                        key={idx}
                      >
                        <li className='border-r border-r-neutral-200 px-6 py-6 font-sans text-sm uppercase text-neutral-500'>
                          {t(footerItem?.title)}
                        </li>
                        {footerItem?.items.map((footerItemSub) => (
                          <li key={footerItemSub.href}>
                            <Link
                              ga-tracking-value={`menu__${content.type}__footer__${getLastSegment(footerItemSub.title)}`}
                              ga-tracking-label={t(footerItemSub.title)}
                              href={footerItemSub.href}
                              className='line-clamp-1 px-4 py-6 font-sans text-base font-medium uppercase text-neutral-900 transition-colors hover:text-primary'
                            >
                              {t(footerItemSub.title)}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    ))}
                  </div>

                  <FeaturedContent
                    featured={content?.featured}
                    type={content.type}
                  />
                </div>
              </NavigationMenuContent>
            </NavigationMenuItem>
          ))}

          {Object.entries(navConfigCollection).map(([category, content]) => (
            <NavigationMenuItem role='menuitem' key={category}>
              <NavigationMenuTrigger asChild>
                <Link
                  href={content.href}
                  className={cn(
                    navigationMenuTriggerStyle(),
                    activeKey === category && activeTriggerClass
                  )}
                  aria-label={`Mở menu danh mục: ${category}`}
                  ga-tracking-value={`menu__${content.type}__${getLastSegment(category)}`}
                  ga-tracking-label={t(category)}
                >
                  {t(category)}
                </Link>
              </NavigationMenuTrigger>

              <NavigationMenuContent className='w-full'>
                <div className='flex w-full bg-background'>
                  <div className='flex basis-[72%] flex-col justify-between'>
                    <div className='flex flex-1'>
                      {content.sections.map((section, idx) => (
                        <div
                          key={idx}
                          className='flex-1 border-l-neutral-300 px-10 pb-8 pt-8 last-of-type:border-l 2xl:px-20 2xl:pb-17.5'
                        >
                          <Link
                            href={section.href}
                            className='w-fit'
                            ga-tracking-value={`menu__${content.type}__${getLastSegment(section.title)}`}
                            ga-tracking-label={t(section.title)}
                          >
                            <h4 className='group flex items-center whitespace-nowrap text-sm font-bold uppercase text-neutral-900 transition-colors duration-300 hover:text-primary 2xl:text-base'>
                              {t(section.title)}
                              <MoveRight className='ml-3 text-primary transition-transform duration-200 group-hover:translate-x-2' />
                            </h4>
                          </Link>
                          <ul className='mt-5 grid grid-cols-2 gap-x-5 gap-y-5'>
                            {section.items.map((item) => (
                              <li key={item.href}>
                                <Link
                                  href={item.href}
                                  className='group flex items-center gap-2.5 text-neutral-600 transition-colors duration-200 hover:text-primary'
                                  ga-tracking-value={`menu__${content.type}__${getLastSegment(item.title)}`}
                                  ga-tracking-label={t(item.title)}
                                >
                                  <Image
                                    src={ImageHelper.getImageUrl(
                                      item.img,
                                      '',
                                      96
                                    )}
                                    width={40}
                                    height={40}
                                    alt={t(item.title)}
                                    className='size-10 shrink-0 rounded-full object-cover transition-transform duration-300 ease-in-out group-hover:scale-95'
                                  />
                                  <p className='line-clamp-2 font-sans text-sm 2xl:text-base'>
                                    {t(item.title)}
                                  </p>
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                    {content?.footer?.map((footerItem, idx) => (
                      <ul
                        className='flex items-center bg-neutral-100 px-10 md:px-20'
                        key={idx}
                      >
                        <li className='border-r border-r-neutral-200 px-6 py-6 font-sans text-sm uppercase text-neutral-500'>
                          {t(footerItem?.title)}
                        </li>
                        {footerItem?.items.map((footerItemSub) => (
                          <li key={footerItemSub.href}>
                            <Link
                              ga-tracking-value={`menu__${content.type}__footer__${getLastSegment(footerItemSub.title)}`}
                              ga-tracking-label={t(footerItemSub.title)}
                              href={footerItemSub.href}
                              className='line-clamp-1 px-4 py-6 font-sans text-base font-medium uppercase text-neutral-900 transition-colors hover:text-primary'
                            >
                              {t(footerItemSub.title)}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    ))}
                  </div>

                  <FeaturedContent
                    featured={content?.featured}
                    type={content.type}
                  />
                </div>
              </NavigationMenuContent>
            </NavigationMenuItem>
          ))}

          {Object.entries(navConfigAccessories).map(([category, content]) => (
            <NavigationMenuItem role='menuitem' key={category}>
              <Link
                href={content.href}
                className={cn(
                  navigationMenuTriggerStyle(),
                  activeKey === category && activeTriggerClass
                )}
                aria-label={`Mở Trang: ${category}`}
                ga-tracking-value={`menu__${getLastSegment(category)}`}
                ga-tracking-label={t(category)}
              >
                {t(category)}
              </Link>
            </NavigationMenuItem>
          ))}

          <NavigationMenuItem role='menuitem'>
            <Link
              href={OUTLET_HREF}
              className={cn(
                navigationMenuTriggerStyle(),
                'relative font-medium text-red-600 before:bg-red-600',
                activeKey === 'outlet' && activeTriggerClass
              )}
              ga-tracking-value='menu__outlet'
              ga-tracking-label={t('nav.section.outlet')}
            >
              <p className='absolute right-3 top-2 text-xxs text-red-600'>
                -50%
              </p>
              {t('nav.section.outlet')}
            </Link>
          </NavigationMenuItem>

          {/* <NavigationMenuItem role='menuitem'>
            <NavigationMenuTrigger asChild>
              <Link
                href={navConfigCareAndShare.href}
                className={navigationMenuTriggerStyle()}
                aria-label={`Đi tới trang ${t(navConfigCareAndShare.title)}`}
                ga-tracking-value={`menu__care_and_share`}
                ga-tracking-label={t(navConfigCareAndShare.title)}
              >
                {t(navConfigCareAndShare.title)}
              </Link>
            </NavigationMenuTrigger>

            <NavigationMenuContent className='flex w-full bg-background'>
              <div className='flex flex-1 justify-end px-20 pb-12 pt-10'>
                <div className='flex max-w-[720px] flex-col items-center'>
                  <p className='text- max-w-[600px] text-center font-sans text-base text-neutral-900/70'>
                    {t(navConfigCareAndShare.description)}
                  </p>

                  <Image
                    src={ImageHelper.getImageUrl(
                      t(navConfigCareAndShare.imageStep),
                      '',
                      1450
                    )}
                    alt={
                      t(navConfigCareAndShare.imageStepAlt) || 'Các bước xử lý'
                    }
                    width={725}
                    height={110}
                    unoptimized
                    className='mt-5 w-full object-contain 2xl:max-h-[150px] 2xl:object-contain'
                  />

                  <Button className='group mt-8' size='large' asChild>
                    <Link
                      href={navConfigCareAndShare.href}
                      ga-tracking-value={`menu__care_and_share__cta`}
                      ga-tracking-label={t(navConfigCareAndShare.buttonText)}
                    >
                      {t(navConfigCareAndShare.buttonText)}
                      <MoveRight className='text-light transition-transform duration-300 group-hover:translate-x-2' />
                    </Link>
                  </Button>
                </div>
              </div>

              <div className='grid basis-2/5 gap-5 border-l border-l-neutral-300 px-8 pb-6 pt-8 xl:pb-12 xl:pt-10 2xl:px-20'>
                <CareAndShareFeaturedContent
                  image={navConfigCareAndShare.image}
                  title={navConfigCareAndShare.title}
                  alt={t(navConfigCareAndShare.imageAlt)}
                />
              </div>
            </NavigationMenuContent>
          </NavigationMenuItem> */}
        </NavigationMenuList>
      </NavigationMenu>
    </nav>
  )
}

// const CareAndShareFeaturedContent = ({
//   image,
//   alt,
// }: {
//   image: string
//   title: string
//   alt: string
// }) => {
//   const { data: apiBanners } = useBannerQuery()
//   const bannersForType = apiBanners?.careAndShare
//   const validBannersFromApi = bannersForType?.filter(
//     (banner) => banner.link && banner.title && banner.image?.desktop
//   )
//   const hasApiBanners = validBannersFromApi && validBannersFromApi.length > 0
//   if (hasApiBanners) {
//     const banner = validBannersFromApi[0]
//     return (
//       <Link href={banner.link} className='block h-full w-full'>
//         <Image
//           width={450}
//           height={262}
//           src={ImageHelper.getImageUrl(banner.image.desktop)}
//           alt={banner.alt || 'Hình ảnh Care & Share'}
//           className='aspect-[340/185] h-full w-full rounded-lg object-cover'
//         />
//       </Link>
//     )
//   }

//   return (
//     <Image
//       width={450}
//       height={262}
//       src={ImageHelper.getImageUrl(image)}
//       alt={alt || 'Hình ảnh nổi bật'}
//       className='aspect-[340/185] h-full w-full rounded-lg object-cover'
//     />
//   )
// }
